import { useCallback, useMemo, useRef, useSyncExternalStore } from 'react'

import { useAvailableApps } from 'modules/app-registry/hooks/useAvailableApps'
import { useWindowStore } from 'modules/desktop/pages/MainPage/stores'
import { getNonAppPageInfo } from 'shared/hooks/analytics/getNonAppPageInfo'
import { useActiveApp } from 'shared/hooks/useActiveApp'
import { useUserContext } from 'shared/hooks/useUserContext'
import {
  getAnalyticsFrames,
  subscribeAnalyticsFrames
} from 'shared/services/analytics/navigation/analyticsFrames'
import { getAnalyticsNavigation } from 'shared/services/analytics/navigation/analyticsNavigation'

import { AnalyticsMeasurementTracker } from './AnalyticsMeasurementTracker/AnalyticsMeasurementTracker'
import { ANALYTICS_TRACKING_SOURCE } from './AnalyticsTracker.constants'
import type { AnalyticsTrackerProps } from './AnalyticsTracker.types'
import {
  canTrackAnalyticsPage,
  isAuthAnalyticsPath
} from './AnalyticsTracker.utils'
import { FrameTracker } from './FrameTracker/FrameTracker'
import { resolveAnalyticsApp } from './resolveAnalyticsApp'
import { useAnalyticsParentOwner } from './useAnalyticsParentOwner'
import { useAuthViewIdentity } from './useAuthViewIdentity'

// Follows the browser URL rather than the shell router, so microfrontends with
// their own router are counted too.
const OwnedAnalyticsTracker = ({ enabled = true }: AnalyticsTrackerProps) => {
  const observer = useMemo(() => getAnalyticsNavigation(), [])
  const route = useSyncExternalStore(observer.subscribe, observer.getSnapshot)
  const frames = useSyncExternalStore(
    subscribeAnalyticsFrames,
    getAnalyticsFrames
  )
  const { apps, isLoading } = useAvailableApps()
  const activeApp = useActiveApp()
  const activeWindowIsLocal = useWindowStore(state =>
    Boolean(
      state.windows.find(w => w.id === state.activeWindowId)?.localComponent
    )
  )
  const { user } = useUserContext()
  const context = {
    userId: user?.id,
    organizationId: user?.organization_id,
    originalOrganizationId: user?.original_organization_id
  }
  const isAuthPage = isAuthAnalyticsPath(route.pathname)
  const activeFrame = frames.find(frame => frame.active && frame.visible)
  const app = resolveAnalyticsApp(route.pathname, Object.values(apps))
  // A local window has no frame, so the shell view carries its app. An iframe
  // window is tracked through its frame; until that registers, the current
  // view continues.
  const localApp =
    activeApp?.type === 'window' && activeWindowIsLocal ? activeApp.app : null
  const nonAppPage = getNonAppPageInfo(route.pathname)
  const appInfo = localApp
    ? { appId: localApp.id, appName: localApp.name }
    : app
      ? { appId: app.id, appName: app.name }
      : nonAppPage
  // Only a path the app registry might claim waits for it. Shell pages
  // (desktop, 2FA setup, public/guest) start at once, so a page left before the
  // registry loads, like /2fa-setup right after sign-in, still counts.
  const canTrack =
    !activeFrame &&
    (!isLoading || isAuthPage || nonAppPage.appId !== 'unknown') &&
    canTrackAnalyticsPage({ pathname: route.pathname, ...context })
  // Freeze only while this auth view is measured, so it keeps the identity its
  // measurement started with rather than one seen while tracking was off.
  const viewIdentity = useAuthViewIdentity(
    enabled && canTrack && isAuthPage
      ? `${route.revision}:${route.path}`
      : null,
    context
  )
  const foreground = useCallback(
    () =>
      document.visibilityState === 'visible' &&
      document.hasFocus() &&
      !getAnalyticsFrames().some(
        frame => frame.iframe === document.activeElement
      ),
    []
  )
  return (
    <>
      <AnalyticsMeasurementTracker
        enabled={enabled}
        view={
          canTrack
            ? {
                ...viewIdentity,
                ...appInfo,
                appPath: route.path,
                path: route.path,
                title: appInfo.appName,
                pageLocation: route.location,
                pageReferrer: route.referrer,
                trackingSource: ANALYTICS_TRACKING_SOURCE
              }
            : null
        }
        foreground={foreground}
        viewVersion={route.revision}
      />
      {frames.map(frame => {
        const targetApp =
          frame.route &&
          resolveAnalyticsApp(frame.route.pathname, Object.values(apps))
        const authPage =
          frame.route && isAuthAnalyticsPath(frame.route.pathname)
            ? getNonAppPageInfo(frame.route.pathname)
            : null
        const trackedFrame = targetApp
          ? { ...frame, appId: targetApp.id, title: targetApp.name }
          : authPage
            ? { ...frame, appId: authPage.appId, title: authPage.appName }
            : frame
        return (
          <FrameTracker
            key={`${frame.id}:${frame.documentVersion}`}
            frame={trackedFrame}
            enabled={enabled}
            context={context}
          />
        )
      })}
    </>
  )
}

export const AnalyticsTracker = (props: AnalyticsTrackerProps) => {
  const owned = useAnalyticsParentOwner()
  const trackedRef = useRef(false)
  if (owned === false) trackedRef.current = true
  if (!trackedRef.current) return null
  // A parent that answers after the handshake timeout takes over. Disabling
  // (rather than unmounting) discards the unfinished measurement instead of
  // reporting it twice. Known limitation: events this tracker already sent
  // between the timeout and the late answer stay reported by both. That needs a
  // cross-origin nested shell whose parent was blocked for over a second.
  return (
    <OwnedAnalyticsTracker
      {...props}
      enabled={owned === false && (props.enabled ?? true)}
    />
  )
}
